/**
 * Conjuntos elétricos da Neoenergia Pernambuco — indicadores de continuidade.
 *
 * A camada `neoenergia_pe_conj_dgc` traz, para cada conjunto, uma coluna por
 * ano de cada indicador (DGC, DEC/FEC apurado e DEC/FEC limite). O DGC do ano
 * de referência define a cor do polígono, tanto no SLD publicado no GeoServer
 * quanto no estilo client-side desta aplicação — as duas faixas precisam ser
 * mantidas em sincronia.
 */

export const CONJ_DGC_LAYER_NAME = "neoenergia_pe_conj_dgc";

export const DGC_YEARS = [
  2016, 2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025,
] as const;

/** Ano usado para colorir os polígonos (mesmo campo do SLD). */
export const DGC_REFERENCE_YEAR = 2025;

export const DGC_NO_DATA_COLOR = "#ffffff";

export interface DgcClass {
  /** Limite superior inclusivo da faixa; `null` na última (aberta). */
  max: number | null;
  color: string;
  label: string;
}

export const DGC_CLASSES: DgcClass[] = [
  { max: 0.9, color: "#2166ac", label: "DGC ≤ 0,9" },
  { max: 1.0, color: "#00c8d7", label: "DGC 0,9 – 1,0" },
  { max: 1.1, color: "#f2d024", label: "DGC 1,0 – 1,1" },
  { max: 1.2, color: "#f08c1e", label: "DGC 1,1 – 1,2" },
  { max: null, color: "#d7191c", label: "DGC > 1,2" },
];

/** Cor da faixa do DGC; branco quando o conjunto não tem dado no ano. */
export function getDgcColor(value: unknown): string {
  // `Number("")` é 0, então a string vazia precisa cair antes da conversão —
  // senão um conjunto sem dado sairia azul em vez de branco.
  if (value === null || value === undefined || value === "") {
    return DGC_NO_DATA_COLOR;
  }

  const numeric = typeof value === "string" ? Number(value) : value;

  if (typeof numeric !== "number" || !Number.isFinite(numeric)) {
    return DGC_NO_DATA_COLOR;
  }

  const found = DGC_CLASSES.find((c) => c.max === null || numeric <= c.max);
  return found ? found.color : DGC_NO_DATA_COLOR;
}

export interface DgcYearRow {
  year: number;
  dgc: number | null;
  decApurado: number | null;
  decLimite: number | null;
  fecApurado: number | null;
  fecLimite: number | null;
  /** Unidades consumidoras médias — peso da agregação estadual. */
  ccMedio: number | null;
}

function toNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

/** Converte as colunas achatadas por ano na série usada pelo gráfico. */
export function buildDgcSeries(
  properties: Record<string, any> | undefined | null
): DgcYearRow[] {
  if (!properties) return [];

  return DGC_YEARS.map((year) => ({
    year,
    dgc: toNumberOrNull(properties[`DGC_${year}`]),
    decApurado: toNumberOrNull(properties[`DEC_APU_${year}`]),
    decLimite: toNumberOrNull(properties[`DEC_LIM_${year}`]),
    fecApurado: toNumberOrNull(properties[`FEC_APU_${year}`]),
    fecLimite: toNumberOrNull(properties[`FEC_LIM_${year}`]),
    ccMedio: toNumberOrNull(properties[`CC_MED_${year}`]),
  }));
}

/**
 * Agrega os conjuntos num único indicador estadual, ano a ano.
 *
 * DEC e FEC são médias por unidade consumidora, então o agregado do estado é a
 * média ponderada pelo número de consumidores do conjunto (`CC_MED`) — a média
 * simples daria o mesmo peso a um conjunto de 860 e a outro de 101 mil
 * consumidores. Conjuntos sem dado no ano ficam fora do numerador e do
 * denominador daquele ano.
 */
export function aggregateStateSeries(
  featureProperties: Array<Record<string, any>>
): DgcYearRow[] {
  const perFeature = featureProperties.map(buildDgcSeries);

  return DGC_YEARS.map((year, yearIndex) => {
    const rows = perFeature
      .map((series) => series[yearIndex])
      .filter((row): row is DgcYearRow => !!row);

    const weightedMean = (pick: (row: DgcYearRow) => number | null) => {
      let weighted = 0;
      let weight = 0;

      rows.forEach((row) => {
        const value = pick(row);
        // Sem peso conhecido o conjunto não entra: incluí-lo com peso 1
        // distorceria a média muito mais do que omiti-lo.
        if (value === null || row.ccMedio === null || row.ccMedio <= 0) return;
        weighted += value * row.ccMedio;
        weight += row.ccMedio;
      });

      return weight > 0 ? weighted / weight : null;
    };

    const consumers = rows.reduce(
      (total, row) => total + (row.ccMedio ?? 0),
      0
    );

    return {
      year,
      dgc: weightedMean((row) => row.dgc),
      decApurado: weightedMean((row) => row.decApurado),
      decLimite: weightedMean((row) => row.decLimite),
      fecApurado: weightedMean((row) => row.fecApurado),
      fecLimite: weightedMean((row) => row.fecLimite),
      ccMedio: consumers > 0 ? consumers : null,
    };
  });
}
